import React, {useEffect, useState, useCallback, useMemo, useRef} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import StandardReports from '../../StandardReports';
import {
  ISearchTermSavePayload,
  Reports,
  RightPanelConfig,
} from '../../config/reportTypes';
import {mergeApiDataIntoConfig, reportApiMap} from '../../common/ReportHelper';
import {
  useSearchTermAddMessage,
  useSearchTermSelectedCount,
  useExportReviewerCounts,
  useLeftPanelReport,
  useNavigationMasterList,
} from '../../../../../hooks/reporting/useReporting';
import {
  SearchTermAddMessageRequestPayload,
  SearchTermJsonResultParams,
  SearchTermTotalSelectedCountRequestPayload,
  ReviewerCountsExportRequest,
  NavigationMasterListResponse,
} from '../../../../../api/types/reportingApi.type';
import {getSearchTermResultJson} from '../../../../../api/endpoints/reportingApi';
import {getColumns} from './column';
import dayjs from 'dayjs';
import {SourceTypeCode, ToastType} from '../../../../../shared/constants';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {GridSortModel} from '@mui/x-data-grid';
import _ from 'lodash';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {t} from 'i18next';
import ArrWaitToCompleteModalWrapper from '../../component/waittocomplete-modal-wrapper/ArrWaitToCompleteModalWrapper';
import './SearchTermReportContainer.scss';

interface ReportConfig {
  infoContent: string | string[] | undefined;
  title: string;
  showInfoIcon?: boolean;
  config: Reports;
}

export type ActionMenuPayloadType = {
  sourceTypeCode: string;
  stR_SelectedSearchIds: any;
  sourceDetails: {
    s: any;
    p?: boolean;
    e?: boolean;
    n?: boolean;
    f?: boolean;
    up?: boolean;
  }[];
  reportId?: number;
};

const DEFAULT_PAGE_SIZE = 50;

interface PageSortConfigType {
  pageSize: number;
  pageNumber: number;
  sort: GridSortModel;
}

const SearchTermReportContainer: React.FC = () => {
  const navigate = useNavigate();
  const [reportConfig, setReportConfig] = useState<ReportConfig | null>(null);
  const [dynamicData, setDynamicData] = useState<any>(null);
  const [searchTermResult, setSearchTermResult] = useState<any>(null);
  const [searchTermAddMessageResponse, setSearchTermAddMessageResponse] =
    useState<any>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiLoading, setApiLoading] = useState<boolean>(false);
  const [checkboxLoading, setCheckboxLoading] = useState<boolean>(false);
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(
    new Set()
  );
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  const [totalSelectedCount, setTotalSelectedCount] = useState<
    number | undefined
  >(0);
  const [sourceDetails, setSourceDetails] = useState<
    {s: any; p?: boolean; e?: boolean; n?: boolean; f?: boolean; up?: boolean}[]
  >([]);

  const [isExporting, setIsExporting] = useState(false);
  const [paginationSortConfig, setPaginationConfig] =
    useState<PageSortConfigType>({
      pageSize: DEFAULT_PAGE_SIZE,
      pageNumber: 1,
      sort: [{field: 'searchNumber', sort: 'asc'}],
    });
  const [actionMenuPayload, setActionMenuPayload] =
    useState<ActionMenuPayloadType | null>(null);
  const paginationSortConfigRef =
    useRef<PageSortConfigType>(paginationSortConfig);
  const hasAppliedInitialValues = useRef(false);
  const location = useLocation();
  const {
    item,
    reportId,
    fromReportLanding = false,
    redirectResponse,
  } = location.state ?? {};
  const [saveReportPayload, setSaveReportPayload] =
    useState<ISearchTermSavePayload>({
      reportTypeCode: '',
      selected_SearchID: [],
      pageNumber: 0,
      pageSize: 0,
      selectedCustomColumns: [5],
      selectedCustomColumnName: [],
      isShared: false,
      isSavedSearchSource: false,
      reportId: reportId || 0,
      searchTermReportType: '',
      searchData: [],
    });

  const selectedRowIdsRef = useRef<Set<string>>(selectedRowIds);
  const selectedColumnsRef = useRef<Set<string>>(selectedColumns);

  useEffect(() => {
    selectedRowIdsRef.current = selectedRowIds;
  }, [selectedRowIds]);

  useEffect(() => {
    selectedColumnsRef.current = selectedColumns;
  }, [selectedColumns]);

  const [dataAsOf, setDataAsOf] = useState<string>(() => {
    if (searchTermAddMessageResponse?.backgroundModel?.dataAsOf) {
      return dayjs
        .utc(searchTermAddMessageResponse.backgroundModel.dataAsOf)
        .format('YYYY/MM/DD HH:mm:ss');
    }
    return dayjs.utc().format('YYYY/MM/DD HH:mm:ss');
  });
  const reportKey = location.pathname.split('/')[2];
  const viewKey = location.pathname.split('/')[3];

  const isSearchTermViewRoute = useMemo(() => {
    return reportKey === 'term' && viewKey === 'view';
  }, [reportKey, viewKey]);

  const reportIdFromUrl = useMemo(() => {
    if (!isSearchTermViewRoute) return 0;
    const params = new URLSearchParams(location.search);
    const id =
      params.get('ReportID') ??
      params.get('reportId') ??
      params.get('ReportId') ??
      params.get('reportID') ??
      '';
    const parsed = Number(id);
    return Number.isFinite(parsed) ? parsed : 0;
  }, [location.search, isSearchTermViewRoute]);

  const resolvedReportKey = useMemo(() => {
    if (reportKey === 'term') return 'SearchTermReport';
    return reportKey;
  }, [reportKey]);

  const leftPanelReportId = useMemo(() => {
    return isSearchTermViewRoute && reportIdFromUrl > 0
      ? reportIdFromUrl
      : (undefined as any);
  }, [isSearchTermViewRoute, reportIdFromUrl]);

  const {data: leftPanelReportData} = useLeftPanelReport(leftPanelReportId);

  const leftPanelResolvedReport = useMemo(() => {
    return (
      (leftPanelReportData as any)?.item ??
      (leftPanelReportData as any)?.report ??
      (leftPanelReportData as any)?.reportModel ??
      (leftPanelReportData as any)?.data ??
      leftPanelReportData ??
      null
    );
  }, [leftPanelReportData]);

  const loadReportConfig = useCallback(async () => {
    const keyToUse = isSearchTermViewRoute ? resolvedReportKey : reportKey;
    if (!keyToUse) return;
    try {
      const data = await import(`../../config/${keyToUse}.json`);
      setReportConfig(data.report);
    } catch (error: any) {
      console.error('Error loading JSON:', error);
      triggerToast(error?.response?.data?.title ?? 'Error loading JSON:', [
        ToastType.Error,
      ]);
    }
  }, [isSearchTermViewRoute, resolvedReportKey, reportKey]);

  const loadDynamicData = useCallback(async () => {
    const keyToUse = isSearchTermViewRoute ? resolvedReportKey : reportKey;
    const apiFn = reportApiMap[keyToUse];
    if (apiFn) {
      try {
        const data = await apiFn(
          redirectResponse?.length ? redirectResponse : []
        );
        setDynamicData(data);
      } catch (error: any) {
        console.error('Error fetching API data:', error);
        triggerToast(
          error?.response?.data?.title ?? 'Error fetching API data:',
          [ToastType.Error]
        );
      }
    }
  }, [isSearchTermViewRoute, resolvedReportKey, reportKey]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      localStorage.removeItem('VisibleColumns');
      await Promise.all([loadReportConfig(), loadDynamicData()]);
      setLoading(false);
    };
    fetchData();
  }, [loadReportConfig, loadDynamicData]);

  // Navigate to Export Page
  const navigateToExport = (response: NavigationMasterListResponse) => {
    setCheckboxLoading(false);
    navigate('/Report/ExportData', {
      state: {
        item: {
          navigationSourceType: SourceTypeCode.SEARCHTERM,
          sourceName: SourceTypeCode.SEARCHTERM,
          source: response?.reportMasterDoclistId?.toString(),
          sourceDisable: true,
        },
      },
    });
  };
  // ------------

  const {mutateAsync: searchTermAddMessage} = useSearchTermAddMessage();
  const {mutateAsync: searchTermSelectedCount} = useSearchTermSelectedCount();
  const {mutateAsync: runReviewerCountsExport} = useExportReviewerCounts();
  const {mutateAsync: getNavigationMasterList} = useNavigationMasterList(
    navigateToExport,
    (error) => {
      setCheckboxLoading(false);
      console.error('Error fetching Navigation Master List', error);
    }
  );

  const NAME_COLUMNS = new Set(['SearchName', 'Content', 'SearchPath']);
  const resolveSortingColumn = (field?: string) => {
    if (!field) return 'SearchNo';
    return NAME_COLUMNS.has(field) ? field : 'SearchNo';
  };

  const triggerToast = (message: string, status: ToastType[]) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };
  const displayMessage = `Data as of ${dataAsOf} UTC`;

  const RIGHT_PANEL_HEADER_CONFIG: RightPanelConfig = {
    title: 'Total Results (All Pages)',
    subTitle: displayMessage,
    showUploadIcon: true,
    showSaveIcon: true,
    showColumnsIcon: true,
    showActionButton: true,
  };

  const handleTileChange = async (key: string, selected: boolean) => {
    try {
      setCheckboxLoading(true);
      setSelectedRowIds((prev) => {
        const next = new Set(prev);
        if (selected) {
          searchTermResult.data.forEach((row: any) =>
            next.add(`${row[1]}|${key}`)
          );
        } else {
          searchTermResult.data.forEach((row: any) =>
            next.delete(`${row[1]}|${key}`)
          );
        }
        return next;
      });

      const flagMap: Record<string, 'p' | 'e' | 'n' | 'f' | 'up'> = {
        pureHits: 'p',
        families: 'f',
        emailThreads: 'e',
        nearDupes: 'n',
        uniqueHits: 'up',
      };

      const flagKey = flagMap[key];
      if (!flagKey) return;

      let updatedDetails = [...sourceDetails];

      const sourceArray = saveReportPayload.selected_SearchID?.length
        ? saveReportPayload.selected_SearchID
        : searchTermResult.data;

      if (!selected && flagKey === 'up') {
        updatedDetails = updatedDetails.map((item) => {
          const newItem = {...item};
          delete newItem.up;
          return newItem;
        });
      } else {
        sourceArray.forEach((row: any) => {
          const id = typeof row === 'string' ? row : row[1];
          const existingIndex = updatedDetails.findIndex(
            (item) => item.s === id
          );

          if (selected) {
            if (flagKey === 'up') {
              updatedDetails.push({s: id, up: true});
            } else {
              if (existingIndex >= 0) {
                updatedDetails[existingIndex][flagKey] = true;
              } else {
                updatedDetails.push({s: id, [flagKey]: true});
              }
            }
          } else {
            if (existingIndex >= 0) {
              delete updatedDetails[existingIndex][flagKey];
            }
          }
        });
      }
      updatedDetails = updatedDetails.filter(
        (item) => Object.keys(item).length > 1
      );
      if (updatedDetails.length === 0) {
        updatedDetails = [];
      }
      setSourceDetails(updatedDetails);

      const totalSelectedCountPayload = {
        sourceTypeCode: 'SEARCHTERM',
        stR_SelectedSearchIds: saveReportPayload.selected_SearchID.map(
          (id: string) => String(id)
        ),
        sourceDetails: updatedDetails,
      };

      const response = await searchTermSelectedCount(totalSelectedCountPayload);
      setActionMenuPayload({
        ...totalSelectedCountPayload,
        reportId: saveReportPayload.reportId,
      });

      setTotalSelectedCount(response?.totalSelectedCount ?? 0);
    } catch (error: any) {
      triggerToast(error?.response?.data?.title ?? 'Something went wrong.', [
        ToastType.Error,
      ]);
    } finally {
      setCheckboxLoading(false);
    }
  };

  const handleCheckboxClick = useCallback(
    async (row: any, checked: boolean, columnKey: string) => {
      try {
        setCheckboxLoading(true);

        const cellKey = `${row.searchNumber}|${columnKey}`;
        setSelectedRowIds((prev) => {
          const next = new Set(prev);
          checked ? next.add(cellKey) : next.delete(cellKey);
          return next;
        });

        const flagMap: Record<string, 'p' | 'e' | 'n' | 'f' | 'up'> = {
          pureHits: 'p',
          families: 'f',
          emailThreads: 'e',
          nearDupes: 'n',
          uniqueHits: 'up',
        };

        const flagKey = flagMap[columnKey];
        const updated = await new Promise<any[]>((resolve) => {
          setSourceDetails((prev) => {
            let list = [...prev];

            if (checked && flagKey) {
              if (flagKey === 'up') {
                list.push({s: row.searchNumber, [flagKey]: true});
              } else {
                const existingIndex = list.findIndex(
                  (item) => item.s === row.searchNumber && !item.up
                );

                if (existingIndex >= 0) {
                  list[existingIndex] = {
                    ...list[existingIndex],
                    [flagKey]: true,
                  };
                } else {
                  list.push({s: row.searchNumber, [flagKey]: true});
                }
              }
            } else if (flagKey) {
              list = list
                .map((item) => {
                  if (item.s === row.searchNumber && item[flagKey]) {
                    if (flagKey === 'up') return null;
                    const {[flagKey]: _, ...rest} = item;
                    return Object.keys(rest).length > 1 ? rest : null;
                  }
                  return item;
                })
                .filter(Boolean);
            }

            resolve(list);
            return list;
          });
        });

        const selectedSearchIds = Array.from(
          new Set(updated.map((i) => String(i.s)))
        );

        const totalSelectedCountPayload = {
          sourceTypeCode: 'SEARCHTERM',
          stR_SelectedSearchIds: selectedSearchIds,
          sourceDetails: updated,
        };
        const response = await searchTermSelectedCount(
          totalSelectedCountPayload
        );
        setActionMenuPayload({
          ...totalSelectedCountPayload,
          reportId: saveReportPayload.reportId,
        });
        setTotalSelectedCount(response?.totalSelectedCount ?? 0);
      } catch (error: any) {
        triggerToast(error?.response?.data?.title ?? 'Something went wrong.', [
          ToastType.Error,
        ]);
      } finally {
        setCheckboxLoading(false);
      }
    },
    [searchTermSelectedCount, sourceDetails, saveReportPayload]
  );

  const columns = useMemo(
    () =>
      getColumns(handleCheckboxClick, selectedRowIdsRef, selectedColumnsRef),
    [handleCheckboxClick]
  );

  const buildSelectedCountPayload = (
    result: any
  ): SearchTermTotalSelectedCountRequestPayload => {
    return {
      sourceTypeCode: 'SEARCHTERM',
      stR_SelectedSearchIds:
        result?.data?.map((item: any) => String(item[1])) ?? [],
      sourceDetails: [],
    };
  };

  const getDescendants = (id: string, data: any[]): string[] => {
    const children = data.filter((item) => item.parent === id);
    return children.reduce<string[]>(
      (acc, child) => [...acc, child.id, ...getDescendants(child.id, data)],
      []
    );
  };

  const fetchSearchTermGridDataWithPagination = useCallback(
    async (
      reportId: number,
      sortModel: GridSortModel,
      paginationConfig: {pageNumber: number; pageSize: number}
    ) => {
      paginationSortConfigRef.current = {
        pageSize: paginationConfig.pageSize,
        pageNumber: paginationConfig.pageNumber,
        sort: sortModel,
      };
      setPaginationConfig(paginationSortConfigRef.current);

      const activeField = sortModel[0]?.field;
      const sortingColumn = resolveSortingColumn(activeField);

      const payload: SearchTermJsonResultParams = {
        ReportId: reportId,
        PageNumber: paginationConfig.pageNumber,
        PageSize: paginationConfig.pageSize,
        SortingColumn: sortingColumn ?? 'SearchNo',
        SortingDirection: sortModel[0]?.sort ?? 'asc',
      };

      try {
        setApiLoading(true);
        const result = await getSearchTermResultJson(payload);
        setSearchTermResult(result);
      } catch (error: any) {
        triggerToast(error?.response?.data?.title || 'Something went wrong', [
          ToastType.Error,
        ]);
      } finally {
        setApiLoading(false);
      }
    },
    [triggerToast]
  );

  const handleAction = useCallback(
    async (actionKey: string, values: Record<string, any>) => {
      if (actionKey === 'applyChanges') {
        try {
          setApiLoading(true);
          setTotalSelectedCount(0);
          setSourceDetails([]);
          setDataAsOf(dayjs.utc().format('YYYY/MM/DD HH:mm:ss'));

          const selectedIds: string[] = (values?.searchesTree ?? []).flatMap(
            (id: string): string[] => {
              const node = dynamicData?.find((item: any) => item.id === id);
              if (!node || node.parent === '#') return [];
              return [id, ...getDescendants(id, dynamicData)];
            }
          );

          const uniqueSelectedIds = Array.from(new Set(selectedIds));
          const numericSelectedIds = uniqueSelectedIds.filter((id) =>
            /^[0-9]+$/.test(id)
          );

          const ensuredSelectedCustomColumns = Array.from(
            new Set([
              ...(saveReportPayload.selectedCustomColumns ?? []),
              5,
              10,
              12,
            ])
          );
          const ensuredSelectedCustomColumnName = Array.from(
            new Set([
              ...(saveReportPayload.selectedCustomColumnName ?? []),
              'CombinedReult',
              'ConceptuallySimilar',
              'UniqueFamilyHits',
            ])
          );

          const payload: SearchTermAddMessageRequestPayload = {
            isAscending: 'DESC',
            pageNumber: 1,
            pageSize: 0,
            reportTypeCode:
              item?.reportTypeCode ||
              leftPanelResolvedReport?.reportTypeCode ||
              'STR010',
            searchTermReportType: '1',
            selectedCustomColumnName:
              leftPanelResolvedReport?.selectedCustomColumnName ??
              Array.from(new Set(ensuredSelectedCustomColumnName)),
            selectedCustomColumns:
              leftPanelResolvedReport?.selectedCustomColumns ??
              Array.from(new Set(ensuredSelectedCustomColumns)),
            selected_SearchID:
              numericSelectedIds.length > 0
                ? numericSelectedIds
                : (leftPanelResolvedReport?.selected_SearchID ??
                  redirectResponse ??
                  []),
            sortingColumn: 'SearchNo',
          };

          const saveResponse = await searchTermAddMessage(payload);

          setSaveReportPayload((prev) => ({
            ...prev,
            pageNumber: 1,
            pageSize: 10,
            isSavedSearchSource: true,
            searchTermReportType:
              saveResponse?.backgroundModel?.reportType ?? 1,
            reportTypeCode:
              item?.reportTypeCode ||
              leftPanelResolvedReport?.reportTypeCode ||
              'STR010',
            selected_SearchID:
              numericSelectedIds.length > 0
                ? numericSelectedIds
                : (leftPanelResolvedReport?.selected_SearchID ??
                  redirectResponse ??
                  []),
            reportId: saveResponse?.backgroundModel?.reportID?.toString() ?? 0,
            selectedCustomColumnName:
              leftPanelResolvedReport?.selectedCustomColumnName ??
              Array.from(new Set(ensuredSelectedCustomColumnName)),
            selectedCustomColumns:
              leftPanelResolvedReport?.selectedCustomColumns ??
              Array.from(new Set(ensuredSelectedCustomColumns)),
            searchData: dynamicData,
          }));

          setSearchTermAddMessageResponse(saveResponse);

          const totalSelectedCountPayload =
            buildSelectedCountPayload(searchTermResult);
          await searchTermSelectedCount(totalSelectedCountPayload);

          setSelectedRowIds(new Set());
          setSelectedColumns(new Set());
        } catch (error: any) {
          triggerToast(error?.response?.data?.title ?? 'Something went wrong', [
            ToastType.Error,
          ]);
        } finally {
          setApiLoading(false);
        }
      }

      if (actionKey === 'reset') {
        setSearchTermResult(null);
      }

      if (actionKey === 'ExportData') {
        setCheckboxLoading(true);
        await getNavigationMasterList({
          sourceInfo: values.payload,
        });
      }
    },
    [
      dynamicData,
      saveReportPayload,
      item,
      leftPanelResolvedReport,
      redirectResponse,
      searchTermAddMessage,
      searchTermSelectedCount,
      searchTermResult,
      triggerToast,
    ]
  );

  const disabledRootIds = useMemo(() => {
    if (!Array.isArray(dynamicData)) return [];

    const targetsByText = new Set([
      'My Saved Search',
      'Shared With Project Administrator',
    ]);

    return dynamicData
      .filter((node: any) => {
        const isRoot = String(node?.parent ?? '') === '#';
        if (!isRoot) return false;

        const text = String(node?.text ?? '').trim();
        const code = String(node?.searchGroupTypeCode ?? '').trim();
        const isTarget =
          targetsByText.has(text) || code === 'MYSEARCH' || code === 'PASHARED';
        if (!isTarget) return false;

        const hasChildren = dynamicData.some(
          (child: any) => String(child?.parent ?? '') === String(node?.id ?? '')
        );
        return !hasChildren;
      })
      .map((node: any) => String(node.id));
  }, [dynamicData]);

  const applyDomDisableForRoots = useCallback(() => {
    if (!disabledRootIds.length) return;

    disabledRootIds.forEach((id) => {
      const labelEl = document.getElementById(`tree-node-label-${id}`);
      if (!labelEl) return;

      const treeItemEl =
        (labelEl.closest('[role="treeitem"]') as HTMLElement | null) ||
        (labelEl.closest('li') as HTMLElement | null);

      if (!treeItemEl) return;

      treeItemEl.setAttribute('aria-disabled', 'true');

      const contentEl =
        (treeItemEl.querySelector(
          '.MuiTreeItem-content'
        ) as HTMLElement | null) ||
        (treeItemEl.querySelector('.tree-item-content') as HTMLElement | null);

      if (contentEl) {
        contentEl.setAttribute('aria-disabled', 'true');
        (contentEl.style as any).pointerEvents = 'none';
        contentEl.classList.add('tree-item-disabled');
      }

      const checkboxEl =
        (treeItemEl.querySelector('.tree-checkbox') as HTMLElement | null) ||
        (treeItemEl.querySelector(
          '[data-testid$="-tree-checkbox"]'
        ) as HTMLElement | null);

      if (checkboxEl) {
        checkboxEl.classList.add('Mui-disabled');
        (checkboxEl.style as any).pointerEvents = 'none';
      }
    });
  }, [disabledRootIds]);

  useEffect(() => {
    if (!disabledRootIds.length) return;

    let observer: MutationObserver | null = null;
    let intervalId: number | undefined;

    const container =
      (document.querySelector(
        '.review-results-container'
      ) as HTMLElement | null) || (document.body as HTMLElement);

    const clickBlocker = (e: MouseEvent) => {
      if (!disabledRootIds.length) return;

      for (const id of disabledRootIds) {
        const labelEl = document.getElementById(`tree-node-label-${id}`);
        if (!labelEl) continue;

        const treeItemEl =
          (labelEl.closest('[role="treeitem"]') as HTMLElement | null) ||
          (labelEl.closest('li') as HTMLElement | null);

        if (treeItemEl && treeItemEl.contains(e.target as Node)) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
    };

    const keyBlocker = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;

      const active = document.activeElement as HTMLElement | null;
      if (!active) return;

      for (const id of disabledRootIds) {
        const labelEl = document.getElementById(`tree-node-label-${id}`);
        if (!labelEl) continue;

        const treeItemEl =
          (labelEl.closest('[role="treeitem"]') as HTMLElement | null) ||
          (labelEl.closest('li') as HTMLElement | null);

        if (treeItemEl && treeItemEl.contains(active)) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
    };

    document.addEventListener('click', clickBlocker, true);
    document.addEventListener('keydown', keyBlocker, true);

    applyDomDisableForRoots();

    observer = new MutationObserver(() => {
      applyDomDisableForRoots();
    });

    try {
      observer.observe(container, {childList: true, subtree: true});
    } catch {
      observer = null;
    }

    // eslint-disable-next-line prefer-const
    intervalId = window.setInterval(() => {
      applyDomDisableForRoots();
    }, 300);

    return () => {
      document.removeEventListener('click', clickBlocker, true);
      document.removeEventListener('keydown', keyBlocker, true);
      if (observer) observer.disconnect();
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [disabledRootIds, applyDomDisableForRoots]);

  const handleColumnToggleAction = async (columnId: string) => {
    try {
      const payload = searchTermResult
        ? buildSelectedCountPayload(searchTermResult)
        : {
            sourceTypeCode: SourceTypeCode.SEARCHNAME,
            stR_SelectedSearchIds: [],
            sourceDetails: [],
          };
      await searchTermSelectedCount(
        payload as SearchTermTotalSelectedCountRequestPayload
      );

      const storedVisibleColumns: string[] = JSON.parse(
        localStorage.getItem('VisibleColumns') || '[]'
      );

      let updatedColumns: string[];
      if (storedVisibleColumns.includes(columnId)) {
        updatedColumns = storedVisibleColumns.filter((col) => col !== columnId);
      } else {
        updatedColumns = [...storedVisibleColumns, columnId];
      }

      const extraColumns = [
        'CombinedReult',
        'ConceptuallySimilar',
        'UniqueFamilyHits',
      ];
      extraColumns.forEach((col) => {
        if (!updatedColumns.includes(col)) {
          updatedColumns.push(col);
        }
      });

      localStorage.setItem('VisibleColumns', JSON.stringify(updatedColumns));

      setSaveReportPayload((prev) => {
        const targetCol = columns.find((col) => col.field === columnId);
        const targetIndex = targetCol?.index;
        if (typeof targetIndex !== 'number') {
          return prev;
        }
        const withoutDefaults = (prev.selectedCustomColumns ?? []).filter(
          (id) => ![5, 10, 12].includes(id)
        );
        let next = [...withoutDefaults];
        if (next.includes(targetIndex)) {
          next = next.filter((id) => id !== targetIndex);
        } else {
          next.push(targetIndex);
        }
        next.push(5, 10, 12);
        next = Array.from(new Set(next));
        return {
          ...prev,
          selectedCustomColumnName: updatedColumns,
          selectedCustomColumns: next,
        };
      });
    } catch (error: any) {
      triggerToast(error?.response?.data?.title ?? 'Something went wrong.', [
        ToastType.Error,
      ]);
    }
  };

  const buildExportPayload = (): ReviewerCountsExportRequest => {
    const sortField = paginationSortConfigRef.current.sort?.[0]?.field;
    const sortDir = paginationSortConfigRef.current.sort?.[0]?.sort;
    return {
      selectionCriteria: {
        reportID: 0,
        selected_SearchID:
          saveReportPayload.selected_SearchID.length > 0
            ? saveReportPayload.selected_SearchID
            : leftPanelResolvedReport?.selected_SearchID,
        reportTypeCode:
          saveReportPayload.reportTypeCode ||
          leftPanelResolvedReport?.reportTypeCode ||
          'STR010',
        pageNumber: paginationSortConfigRef.current.pageNumber ?? 1,
        pageSize: 0, //Page size should be 0 in case of report to fetch all the records.
        isAscending: sortDir ? String(sortDir).toUpperCase() : '',
        sortingColumn: resolveSortingColumn(sortField),
        selectedCustomColumns: leftPanelResolvedReport?.selectedCustomColumns
          ?.length
          ? leftPanelResolvedReport.selectedCustomColumns
          : saveReportPayload.selectedCustomColumns?.length
            ? saveReportPayload.selectedCustomColumns
            : [5, 10, 12],
        selectedCustomColumnName:
          saveReportPayload.selectedCustomColumnName ?? [],
        dataAsOf: dayjs
          .utc(dataAsOf, 'YYYY/MM/DD HH:mm:ss', true)
          .toISOString(),
      },
      reportType:
        saveReportPayload.reportTypeCode ||
        leftPanelResolvedReport?.reportTypeCode ||
        'STR010',
      actionType: 'EXPORTREPORT',
    };
  };

  useEffect(() => {
    let cleanup: (() => void) | undefined;
    let intervalId: number | undefined;
    let attached = false;

    const tryAttach = () => {
      const btn = document.querySelector('[aria-label="Export"]');
      if (!btn || !runReviewerCountsExport) return false;

      const handler = async () => {
        if (isExporting) return;
        setIsExporting(true);
        const payload = buildExportPayload();
        try {
          await runReviewerCountsExport(payload);
          triggerToast(t('reports.common.reportExportSuccess'), [
            ToastType.Success,
          ]);
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (err: any) {
          triggerToast(t('reports.common.reportExportError'), [
            ToastType.Error,
          ]);
        } finally {
          setIsExporting(false);
        }
      };

      btn.addEventListener('click', handler);
      cleanup = () => btn.removeEventListener('click', handler);
      attached = true;
      return true;
    };

    // Attempt immediate attach, else retry briefly
    if (!tryAttach()) {
      let tries = 0;
      intervalId = window.setInterval(() => {
        tries += 1;
        if (tryAttach() || tries > 50) {
          if (intervalId) window.clearInterval(intervalId);
          intervalId = undefined;
        }
      }, 50);
    }

    return () => {
      if (intervalId) window.clearInterval(intervalId);
      if (cleanup && attached) cleanup();
    };
    // Re-run when loading finishes / report config becomes available
  }, [
    runReviewerCountsExport,
    saveReportPayload,
    searchTermAddMessageResponse,
    isExporting,
    loading,
    reportConfig,
  ]);
  const initialValuesFromLeftPanel: string[] = useMemo(() => {
    if (Array.isArray(leftPanelResolvedReport?.selected_SearchID)) {
      return leftPanelResolvedReport.selected_SearchID.map(String);
    }
    if (Array.isArray(redirectResponse)) {
      return redirectResponse.map(String);
    }
    return [];
  }, [leftPanelResolvedReport, redirectResponse]);
  useEffect(() => {
    if (
      !fromReportLanding &&
      !hasAppliedInitialValues.current &&
      initialValuesFromLeftPanel.length !== 0
    ) {
      handleAction('applyChanges', {searchesTree: initialValuesFromLeftPanel});
      hasAppliedInitialValues.current = true;
    }
  }, [redirectResponse]);

  useEffect(() => {
    if (reportId)
      fetchSearchTermGridDataWithPagination(
        reportId,
        paginationSortConfigRef.current.sort,
        {
          pageNumber: paginationSortConfigRef.current.pageNumber,
          pageSize: paginationSortConfigRef.current.pageSize,
        }
      );
  }, [reportId]);

  const mergedReportConfig = useMemo(() => {
    if (!reportConfig?.config || !dynamicData) return null;

    return mergeApiDataIntoConfig(
      reportConfig.config,
      dynamicData,
      initialValuesFromLeftPanel
    );
  }, [reportConfig, dynamicData, initialValuesFromLeftPanel]);

  return (
    <>
      {loading || !reportConfig ? (
        <div
          data-testid="loading-container"
          style={{display: 'flex', justifyContent: 'center', marginTop: '50px'}}
        >
          <ArrLoadingSpinner show={loading} overlay={true} isOpen={true} />
        </div>
      ) : (
        <StandardReports
          title={
            isSearchTermViewRoute && fromReportLanding
              ? `Report: ${item?.label}`
              : reportConfig.title
          }
          showInfoIcon={reportConfig.showInfoIcon}
          infoContent={reportConfig?.infoContent}
          reportsConfig={mergedReportConfig}
          loading={apiLoading}
          onAction={handleAction}
          data={searchTermResult}
          columns={columns}
          mapRows={(data: any) =>
            data?.data?.map((item: any) => ({
              id: item[1],
              SearchName: item[0],
              searchNumber: item[1],
              SearchPath: item[2],
              Content: item[3],
              workProduct: item[4],
              pureHits: item[5] != 'N/A' ? item[5] : item[6],
              families: item[7],
              emailThreads: item[8],
              nearDupes: item[9],
              uniqueHits: item[11],
              inCombinedResults: item[13],
              docsInCombinedResults: item[14],
              nearDupesCombinedResults: item[15],
            })) ?? []
          }
          onFilterChange={(e) => {
            if (
              e.searchesTree?.length ===
              saveReportPayload.updated_search_ID?.length
            )
              return;
            setSaveReportPayload((prev) => ({
              ...prev,
              updated_search_ID: e.searchesTree,
            }));
          }}
          rightPanelHeaderConfig={RIGHT_PANEL_HEADER_CONFIG}
          selectedOption={null}
          isNoData={searchTermResult?.data?.length === 0}
          getRowId={(row: any) => row.searchNumber}
          saveReportConfig={saveReportPayload}
          onColumnToggleAction={handleColumnToggleAction}
          onTileChange={handleTileChange}
          totalSelectedCount={totalSelectedCount}
          savedData={leftPanelResolvedReport}
          gridConfig={{
            className: 'search-term-report-grid-wrapper',
            primaryKey: 'searchNumber',
            totalRecords: searchTermResult?.totalRecords ?? 0,
            pageSizeOptions: [10, 50, 100, 500],
            itemsPerPage: paginationSortConfigRef.current.pageSize,
            currentPage: paginationSortConfigRef.current.pageNumber,
            sortModel: paginationSortConfigRef.current.sort,
            disableColumnResize: false,
            onSortModelChange: (value) => {
              if (_.isEqual(value, paginationSortConfigRef.current.sort))
                return;
              fetchSearchTermGridDataWithPagination(
                saveReportPayload.reportId,
                value,
                {
                  pageNumber: 1,
                  pageSize: paginationSortConfigRef.current.pageSize,
                }
              );
            },
            setItemsPerPage: (value) => {
              fetchSearchTermGridDataWithPagination(
                saveReportPayload.reportId,
                paginationSortConfigRef.current.sort,
                {
                  pageNumber: 1,
                  pageSize: value,
                }
              );
            },
            onPageChange: (pageNumber, pageSize) => {
              if (pageNumber !== paginationSortConfigRef.current.pageNumber) {
                fetchSearchTermGridDataWithPagination(
                  saveReportPayload.reportId,
                  paginationSortConfigRef.current.sort,
                  {
                    pageNumber,
                    pageSize:
                      pageSize ?? paginationSortConfigRef.current.pageSize,
                  }
                );
              }
            },
            getRowHeight: () => 'auto',
            entityType: 'entries',
          }}
          actionMenuPayload={actionMenuPayload || undefined}
        />
      )}

      <ArrLoadingSpinner show={checkboxLoading} overlay={true} isOpen={true} />

      {searchTermAddMessageResponse?.backgroundModel?.notificationID > 0 &&
        searchTermAddMessageResponse?.backgroundModel?.reportID > 0 && (
          <ArrWaitToCompleteModalWrapper
            key={`${searchTermAddMessageResponse?.backgroundModel?.notificationID}-${searchTermAddMessageResponse?.backgroundModel?.reportID}`}
            showClose={false}
            notificationId={
              searchTermAddMessageResponse.backgroundModel.notificationID
            }
            reportId={searchTermAddMessageResponse.backgroundModel.reportID}
            onComplete={(reportId) =>
              fetchSearchTermGridDataWithPagination(
                reportId,
                paginationSortConfigRef.current.sort,
                {
                  pageNumber: paginationSortConfigRef.current.pageNumber,
                  pageSize: paginationSortConfigRef.current.pageSize,
                }
              )
            }
          />
        )}

      {showToast && (
        <div data-testid="toast-notification">
          <ArrToastNotification
            message={toastMessage}
            status={toastStatus}
            onClose={() => setShowToast(false)}
          />
        </div>
      )}
    </>
  );
};

export default SearchTermReportContainer;
